/**
 * Number formatting utilities for Australian currency display
 * 
 * Rules:
 * - Numbers >= 1,000,000: Show as millions (e.g., $1.43M)
 * - Numbers >= 1,000: Show as thousands with commas (e.g., $89,049 or $89K if abbreviated)
 * - Always use commas for thousands separator
 * - Round to 2 decimal places for millions, 0 decimals for thousands
 */

export function formatCurrency(amount, options = {}) {
  const {
    abbreviated = false,
    decimals = null,
    showCents = false
  } = options;

  if (!amount && amount !== 0) return '$0';

  const absAmount = Math.abs(amount);
  const isNegative = amount < 0;
  const prefix = isNegative ? '-$' : '$';

  // Millions
  if (absAmount >= 1000000) {
    const millions = absAmount / 1000000;
    const decimalPlaces = decimals !== null ? decimals : 2;
    return `${prefix}${millions.toFixed(decimalPlaces)}M`;
  }

  // Thousands (abbreviated)
  if (abbreviated && absAmount >= 1000) {
    const thousands = absAmount / 1000;
    const decimalPlaces = decimals !== null ? decimals : (absAmount >= 100000 ? 0 : 1);
    return `${prefix}${thousands.toFixed(decimalPlaces)}K`;
  }

  // Full number with commas
  if (showCents) {
    return `${prefix}${absAmount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  return `${prefix}${Math.round(absAmount).toLocaleString('en-AU')}`;
}

export function formatNumber(num, abbreviated = false) {
  if (!num && num !== 0) return '0';

  const absNum = Math.abs(num);
  
  // Millions
  if (absNum >= 1000000) {
    return `${(absNum / 1000000).toFixed(2)}M`;
  }

  // Thousands (abbreviated)
  if (abbreviated && absNum >= 1000) {
    return `${(absNum / 1000).toFixed(absNum >= 100000 ? 0 : 1)}K`;
  }

  // Full number with commas
  return Math.round(absNum).toLocaleString('en-AU');
}

export function formatPercentage(value, decimals = 1) {
  if (!value && value !== 0) return '0%';
  return `${value.toFixed(decimals)}%`;
}

// Examples:
// formatCurrency(1430000) => "$1.43M"
// formatCurrency(89049) => "$89,049"
// formatCurrency(89049, { abbreviated: true }) => "$89K"
// formatCurrency(1068588) => "$1.07M"
// formatNumber(250) => "250"
// formatNumber(1845) => "1,845"
